import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAttachmentData,
  type PreviewableAttachment,
} from '@/utils/attachmentDownload';

export type { PreviewableAttachment } from '@/utils/attachmentDownload';

// Keep at most this many decoded attachments in memory so gallery navigation
// across large PDFs/spreadsheets does not bloat the tab.
const MAX_CACHE_ENTRIES = 3;

import type { FilePreviewModalProps } from '@/components/common/FilePreviewModal/FilePreviewModal.types';

export interface FilePreviewModalBindings
  extends Omit<FilePreviewModalProps, 'onNavigateNext' | 'onNavigatePrev'> {
  onNavigateNext: () => void;
  onNavigatePrev: () => void;
}

interface UseFilePreviewReturn {
  opened: boolean;
  currentIndex: number;
  data: ArrayBuffer | null;
  loading: boolean;
  error: boolean;
  openPreview: (index: number) => void;
  closePreview: () => void;
  navigateNext: () => void;
  navigatePrev: () => void;
  currentAttachment: PreviewableAttachment | null;
  totalCount: number;
  /** Spread-ready props for FilePreviewModal: `<FilePreviewModal {...preview.modalProps} />` */
  modalProps: FilePreviewModalBindings;
}

export function useFilePreview(
  attachments: PreviewableAttachment[]
): UseFilePreviewReturn {
  const [opened, setOpened] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [data, setData] = useState<ArrayBuffer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Insertion-ordered Map is used as an LRU: on every hit we re-insert to mark
  // the entry as most-recently-used, and we evict from the front when exceeding
  // MAX_CACHE_ENTRIES.
  const cacheRef = useRef<Map<string, ArrayBuffer>>(new Map());

  useEffect(() => {
    const cache = cacheRef.current;
    return () => {
      cache.clear();
    };
  }, []);

  const loadAttachment = useCallback(
    async (attachment: PreviewableAttachment) => {
      const cache = cacheRef.current;
      const cached = cache.get(attachment.id);
      if (cached) {
        cache.delete(attachment.id);
        cache.set(attachment.id, cached);
        setData(cached);
        setLoading(false);
        setError(false);
        return;
      }

      setData(null);
      setLoading(true);
      setError(false);
      try {
        const result = await fetchAttachmentData(attachment);
        cache.set(attachment.id, result);
        while (cache.size > MAX_CACHE_ENTRIES) {
          const oldestKey = cache.keys().next().value;
          if (oldestKey === undefined) {
            break;
          }
          cache.delete(oldestKey);
        }
        setData(result);
      } catch {
        setError(true);
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const openPreview = useCallback(
    (index: number) => {
      const attachment = attachments[index];
      if (!attachment) {
        return;
      }
      setCurrentIndex(index);
      setOpened(true);
      loadAttachment(attachment);
    },
    [attachments, loadAttachment]
  );

  const closePreview = useCallback(() => {
    setOpened(false);
    setData(null);
    setError(false);
    setLoading(false);
  }, []);

  const navigateNext = useCallback(() => {
    setCurrentIndex((previous) => {
      const nextIndex = previous + 1;
      const attachment = attachments[nextIndex];
      if (!attachment) {
        return previous;
      }
      loadAttachment(attachment);
      return nextIndex;
    });
  }, [attachments, loadAttachment]);

  const navigatePrev = useCallback(() => {
    setCurrentIndex((previous) => {
      const prevIndex = previous - 1;
      const attachment = attachments[prevIndex];
      if (!attachment) {
        return previous;
      }
      loadAttachment(attachment);
      return prevIndex;
    });
  }, [attachments, loadAttachment]);

  const currentAttachment = attachments[currentIndex] ?? null;

  const modalProps: FilePreviewModalBindings = {
    opened,
    onClose: closePreview,
    currentIndex,
    totalCount: attachments.length,
    currentAttachment,
    data,
    loading,
    error,
    onNavigateNext: navigateNext,
    onNavigatePrev: navigatePrev,
  };

  return {
    opened,
    currentIndex,
    data,
    loading,
    error,
    openPreview,
    closePreview,
    navigateNext,
    navigatePrev,
    currentAttachment,
    totalCount: attachments.length,
    modalProps,
  };
}
